import { useEffect, useMemo } from "react";

import { PalButton, PalIconButton, PalTooltip, PalTypography } from "@palamar/fe-library";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { CloudQueue, HelpOutline, Info, PanoramaFishEye, Settings, TaskAlt } from "@mui/icons-material";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Box, CircularProgress, Grid, lighten } from "@mui/material";

import { eCommerceDataSourceStatusType, eCommerceDefinitionList, eCommerceProject } from "~constants";
import {
  createOrUpdateECommerceIntegration,
  getECommerceIntegrationDataSourceList,
  getECommerceList,
  getShopifyDeepLink,
} from "~store/settings/eCommerce/actions";
import { selectECommerceDataSourceList } from "~store/settings/eCommerce/selectors";
import { selectProject } from "~store/settings/projectSettings/selectors";

import { IntegrationDetailModal, ManualIntegrationModal, StepContainer } from "../CommonComponents";

export function ECommerceItem({
  image: Image,
  imageStyles,
  name,
  onClick,
  onClickConfigure,
  onClickTest,
  onClickDetail,
  isDone,
  isDisabled,
  hideTestButton = false,
  dataSourceList = [],
}) {
  const dataSourceStatusByDataSourceList = useMemo(() => {
    const isProcessing = dataSourceList.some((item) => item.status === eCommerceDataSourceStatusType.PROCESSING);
    const isProcessed = dataSourceList.some((item) => item.status === eCommerceDataSourceStatusType.PROCESSED);
    const isFresh = dataSourceList.some((item) => item.status === eCommerceDataSourceStatusType.FRESH);
    const isError = dataSourceList.some((item) => item.status === eCommerceDataSourceStatusType.ERROR);
    if (isProcessing) return eCommerceDataSourceStatusType.PROCESSING;
    if (isProcessed) return eCommerceDataSourceStatusType.PROCESSED;
    if (isError) return eCommerceDataSourceStatusType.ERROR;
    if (isFresh) return eCommerceDataSourceStatusType.FRESH;
    return eCommerceDataSourceStatusType.FRESH;
  }, [dataSourceList]);

  const { t } = useTranslation();
  return (
    <Box
      alignItems="center"
      display="flex"
      flexDirection="column"
      spacing={1}
      sx={{
        userSelect: "none",
        border: "1px solid",
        borderColor: "grey.300",
        borderRadius: 1,
        padding: 2,
        height: 120,
        ...(isDone &&
          !isDisabled &&
          (dataSourceStatusByDataSourceList === eCommerceDataSourceStatusType.PROCESSED
            ? {
                backgroundColor: (theme) => lighten(theme.palette.success.light, 0.91),
              }
            : dataSourceStatusByDataSourceList === eCommerceDataSourceStatusType.PROCESSING
              ? {
                  backgroundColor: (theme) => lighten(theme.palette.info.light, 0.91),
                }
              : dataSourceStatusByDataSourceList === eCommerceDataSourceStatusType.ERROR
                ? {
                    backgroundColor: (theme) => lighten(theme.palette.error.light, 0.91),
                  }
                : dataSourceStatusByDataSourceList === eCommerceDataSourceStatusType.FRESH
                  ? {
                      backgroundColor: (theme) => lighten(theme.palette.warning.light, 0.91),
                    }
                  : {
                      backgroundColor: (theme) => lighten(theme.palette.grey[200], 0.91),
                    })),

        ...(isDisabled && {
          backgroundColor: (theme) => lighten(theme.palette.error.light, 0.91),
        }),
      }}
    >
      <Box
        sx={{
          display: "flex",
          position: "relative",
          justifyContent: "center",
          width: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            maxWidth: "calc(100% - 60px)",
            svg: {
              // width: 64,
              height: 36,
              ...imageStyles,
            },
          }}
        >
          <PalTooltip title={name}>
            <Image />
          </PalTooltip>
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: 2,
            right: 2,
          }}
        >
          <PalTooltip
            title={
              isDone
                ? isDisabled
                  ? t("dashboard.onboarding.integration.integrationItem.disabled")
                  : dataSourceStatusByDataSourceList === eCommerceDataSourceStatusType.PROCESSING
                    ? t("dashboard.onboarding.integration.integrationItem.processing")
                    : dataSourceStatusByDataSourceList === eCommerceDataSourceStatusType.ERROR
                      ? t("dashboard.onboarding.integration.integrationItem.error")
                      : dataSourceStatusByDataSourceList === eCommerceDataSourceStatusType.FRESH
                        ? t("dashboard.onboarding.integration.integrationItem.fresh")
                        : t("dashboard.onboarding.integration.integrationItem.connected")
                : t("dashboard.onboarding.integration.integrationItem.disconnected")
            }
          >
            {isDone &&
              !isDisabled &&
              (dataSourceStatusByDataSourceList === eCommerceDataSourceStatusType.PROCESSED ? (
                <TaskAlt color="success" />
              ) : dataSourceStatusByDataSourceList === eCommerceDataSourceStatusType.PROCESSING ? (
                <CircularProgress size={22} />
              ) : dataSourceStatusByDataSourceList === eCommerceDataSourceStatusType.FRESH ? (
                <HelpOutline color="secondary" />
              ) : dataSourceStatusByDataSourceList === eCommerceDataSourceStatusType.ERROR ? (
                <HighlightOffIcon color="error" />
              ) : null)}
            {isDone && isDisabled && <HighlightOffIcon color="error" />}
            {!isDone && <PanoramaFishEye color="secondary" />}
          </PalTooltip>
        </Box>
      </Box>
      <Box alignItems={"center"} display="flex" gap={1} mt={"auto"} width={1}>
        <PalTooltip {...(!isDone && { open: false })} title="Revoke the access from the integration's dashboard">
          <Box
            sx={{
              ...(isDone && {
                cursor: "not-allowed !important",
              }),
            }}
            width={1}
          >
            <PalButton
              fullWidth
              color={onClickConfigure ? "primary" : !isDone ? "primary" : "error"}
              disabled={onClickConfigure ? false : isDone}
              sx={{
                flex: 1,
              }}
              onClick={onClickConfigure || onClick}
              {...(onClickConfigure && { startIcon: <Settings /> })}
              variant="outlined"
            >
              <PalTypography fontWeight={600} variant="body1">
                {onClickConfigure ? "Configure" : isDone ? "Disconnect" : "Connect"}
              </PalTypography>
            </PalButton>
          </Box>
        </PalTooltip>
        {isDone && !hideTestButton && (
          <PalTooltip title="Test the integration">
            <PalButton
              color="primary"
              sx={{ minWidth: "auto", flex: "0 0 auto" }}
              variant="outlined"
              onClick={onClickTest}
            >
              <CloudQueue />
            </PalButton>
          </PalTooltip>
        )}
        {isDone && (
          <PalTooltip title="Show integration details">
            <PalIconButton
              color="primary"
              sx={{ padding: "2px", flex: "0 0 auto" }}
              variant="text"
              onClick={onClickDetail}
            >
              <Info />
            </PalIconButton>
          </PalTooltip>
        )}
      </Box>
    </Box>
  );
}

function StepECommerce({ formik, stepKey, nextAction, setFieldValue }) {
  const project = useSelector(selectProject);
  const dispatch = useDispatch();
  const formikValues = formik.values?.[stepKey];

  const eCommerceDataSourceList = useSelector(selectECommerceDataSourceList);

  useEffect(() => {
    if (project?.id) {
      dispatch(getECommerceList(project.id));
      dispatch(getECommerceIntegrationDataSourceList(project.id));
    }
  }, [dispatch, project?.id]);

  const handleClickShowDetails = (integration) => () => {
    IntegrationDetailModal.show({ integration: integration?.credentials });
  };

  const handleManualIntegration = (integrationName) => async () => {
    const integration = eCommerceDefinitionList.find((item) => item.key === integrationName);
    const inputFields = integration.configParams.map((item) => {
      return {
        ...item,
        label: item.label,
        key: item.key,
        required: item.required,
      };
    });
    const integrationData = formikValues?.[integrationName];
    const state = integrationData?.credentials || {};
    const responseState = await ManualIntegrationModal.show({ integrationName: integration.name, inputFields, state });

    const isDeleteMode = Object.values(responseState).every((item) => !item);
    if (responseState) {
      const newState = {
        ...responseState,
      };
      if (newState?.access_token && newState?.access_token === state?.access_token) {
        delete newState.access_token;
      }
      if (newState?.refresh_token && newState?.refresh_token === state?.refresh_token) {
        delete newState.refresh_token;
      }
      if (newState?.client_secret && newState?.client_secret === state?.client_secret) {
        delete newState.client_secret;
      }
      if (newState?.password && newState?.password === state?.password) {
        delete newState.password;
      }
      if (newState?.member_code && newState?.member_code === state?.member_code) {
        delete newState.member_code;
      }
      const isActiveState = newState?.is_active;
      delete newState.is_active;
      //detect rest fields are changed or not
      let isRestFieldsChanged = false;

      for (const key in newState) {
        if (newState[key] !== state[key]) {
          isRestFieldsChanged = true;
          break;
        }
      }
      await dispatch(
        createOrUpdateECommerceIntegration(project.id, {
          ...integrationData,
          credentials: isRestFieldsChanged ? newState : undefined, //set undefined to override the old credentials to none
          is_active: isDeleteMode ? false : isActiveState,
          platform_type: integrationName,
        })
      );
      await dispatch(getECommerceList(project.id));
      await dispatch(getECommerceIntegrationDataSourceList(project.id));
    }
  };

  useEffect(() => {
    //make request every 10 secs when eCommerceDataSourceList's an item has any processing status
    const interval = setInterval(() => {
      if (eCommerceDataSourceList?.some((item) => item.status === eCommerceDataSourceStatusType.PROCESSING)) {
        dispatch(getECommerceIntegrationDataSourceList(project.id));
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [dispatch, project.id, eCommerceDataSourceList]);

  return (
    <StepContainer>
      <Grid container spacing={2}>
        <Grid item mb={2} xs={12}>
          <PalTypography color="textPrimary" fontWeight={600} variant="h5">
            E-Commerce Integration
          </PalTypography>
          <PalTypography gutterBottom variant="body1">
            Please select the e-commerce platform you would like to integrate with your assistant. This will allow your
            assistant to access your store's extra information and provide better customer service.
          </PalTypography>
        </Grid>
        <Grid item xs={12}>
          <Box
            gap={3}
            sx={{
              borderRadius: 1,
            }}
          >
            <Grid container spacing={2}>
              {/* <Grid item md={6} sm={12} xs={12}>
                <IntegrationItem
                  desc="Connect your Instagram account to chat with your customers."
                  image={InstagramIcon}
                  isDone={formikValues?.instagram?.access_token}
                  name="Instagram"
                  onClick={startInstagramLogin}
                  onClickDetail={handleClickShowDetails(formikValues?.instagram)}
                />
              </Grid> */}
              {eCommerceDefinitionList.map((item) => {
                const integrationItem = formikValues?.[item.key];

                const isAllCredentialsFilled = item.configParams.every((param) => {
                  return integrationItem?.credentials?.[param.key];
                });
                const relatedDataSourceList = eCommerceDataSourceList?.filter(
                  (item) => item.platform_integration?.id === integrationItem?.id
                );

                const isShopifyAndConnected = item.key === eCommerceProject.PS && !!project?.shopify_integration;
                const handleClickGetShopifyDeepLink = async () => {
                  const deepLinkResponse = await dispatch(getShopifyDeepLink(project?.shopify_integration));
                  if (deepLinkResponse?.deep_link) {
                    window.open(deepLinkResponse?.deep_link, "_blank");
                  }
                };
                return (
                  <Grid key={item.key} item md={4} sm={6} xs={12}>
                    <ECommerceItem
                      dataSourceList={relatedDataSourceList}
                      desc={`Connect your ${item.name} account to chat with your customers.`}
                      hideTestButton={isShopifyAndConnected}
                      image={item.logo}
                      imageStyles={item.logoStyles}
                      isDone={isAllCredentialsFilled}
                      name={item.name}
                      onClick={handleManualIntegration(item.key)}
                      onClickDetail={handleClickShowDetails(formikValues?.[item.key])}
                      {...((isAllCredentialsFilled || isShopifyAndConnected) && {
                        onClickConfigure: isShopifyAndConnected
                          ? handleClickGetShopifyDeepLink
                          : handleManualIntegration(item.key),
                      })}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </StepContainer>
  );
}

export default StepECommerce;
